import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useUserRole } from '../hooks/useUserRole'
import UserMenu from './UserMenu'
import './Navigation.css'

interface NavigationProps {
  isMenuOpen: boolean
  setIsMenuOpen: (isOpen: boolean) => void
}

// Hook do sprawdzenia czy jesteśmy na mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.innerWidth <= 768
  })

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return isMobile
}

type NavItem = {
  path: string
  label: string
  dropdown?: Array<{ path: string; label: string }>
  emphasis?: boolean
}

const Navigation: React.FC<NavigationProps> = ({ isMenuOpen, setIsMenuOpen }) => {
  const location = useLocation()
  const { user } = useAuth()
  const { roles } = useUserRole()
  const isMobile = useIsMobile()
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({})

  const isPathActive = (path: string): boolean => {
    // Obsługa pathname + opcjonalnie query/hash (ważne dla /dla-firm#...)
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : 'https://wojskaspecjalne.pl'
      const target = new URL(path, origin)
      const samePath = location.pathname === target.pathname
      if (!samePath) return false

      // Jeśli link ma query - porównaj query
      if (target.search) {
        if (location.search !== target.search) return false
      }

      // Jeśli link ma hash - porównaj hash
      if (target.hash) {
        if (location.hash !== target.hash) return false
      }

      return true
    } catch {
      // Fallback: stara logika tylko po pathname
      const [targetPath] = path.split('#')
      return location.pathname === targetPath
    }
  }

  const isActive = (path: string): string => (isPathActive(path) ? 'active' : '')

  const isAnyDropdownItemActive = (items: Array<{ path: string }>): boolean => {
    return items.some((i) => isPathActive(i.path))
  }

  const handleLinkClick = () => {
    setIsMenuOpen(false)
    setOpenDropdowns({})
  }

  const toggleDropdown = (key: string) => {
    if (isMobile) {
      setOpenDropdowns(prev => ({
        ...prev,
        [key]: !prev[key]
      }))
    }
  }

  const setDropdownOpen = (key: string, isOpen: boolean) => {
    if (!isMobile) {
      setOpenDropdowns(prev => ({
        ...prev,
        [key]: isOpen
      }))
    }
  }

  const isDropdownOpen = (key: string) => openDropdowns[key] || false

  // Wykryj czy jesteśmy na stronie logowania lub rejestracji
  const isLoginPage = location.pathname === '/logowanie'
  const isRegisterPage = location.pathname === '/rejestracja'
  const isAuthPage = isLoginPage || isRegisterPage

  // Sprawdź czy użytkownik jest moderatorem lub adminem
  const isModeratorOrAdmin = roles.some(
    (role) => role.role_name === 'moderator' || role.role_name === 'admin'
  )

  // Różne linki w zależności od strony
  const navLinks: NavItem[] = useMemo(() => {
    if (isLoginPage) return [{ path: '/rejestracja', label: 'Zarejestruj się' }]
    if (isRegisterPage) return [{ path: '/logowanie', label: 'Logowanie' }]

    const links: NavItem[] = [
      {
        path: '/szkolenia',
        label: 'Szkolenia',
        emphasis: true,
        dropdown: [
          { path: '/szkolenia', label: 'Wszystkie szkolenia' },
          { path: '/szkolenia-energokinetyczne', label: 'Energokinetyczne' },
          { path: '/szkolenia-medyczne', label: 'Medyczne' },
          { path: '/wydarzenia', label: 'Terminy' },
          { path: '/pakiety', label: 'Pakiety' },
        ]
      },
      {
        path: '/dla-firm',
        label: 'Dla firm',
      },
      {
        path: '/biblioteka',
        label: 'Biblioteka',
        dropdown: [
          { path: '/biblioteka', label: 'Przeglądaj' },
          { path: '/biblioteka/autorzy', label: 'Autorzy' },
        ]
      },

      // (celowo brak "Rezerwacje i konto" w topbarze — jest w menu użytkownika pod avatarem)
    ]

    return links
  }, [isLoginPage, isRegisterPage, user, isModeratorOrAdmin])

  // Zamknij menu przy zmianie lokacji
  useEffect(() => {
    setIsMenuOpen(false)
  }, [location.pathname, setIsMenuOpen])

  // Blokuj scroll gdy menu otwarte
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMenuOpen])

  return (
    <>
      {/* Hamburger Button - tylko na mobile - WCAG AAA Keyboard Support */}
      <button 
        className="hamburger-button"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label={isMenuOpen ? "Zamknij menu nawigacji" : "Otwórz menu nawigacji"}
        aria-expanded={isMenuOpen}
        aria-controls="navigation"
      >
        <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
        <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
        <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
      </button>

      {/* Backdrop - WCAG AAA Keyboard Support */}
      {isMenuOpen && (
        <div 
          className="mobile-backdrop"
          onClick={() => setIsMenuOpen(false)}
          role="presentation"
          aria-hidden="true"
        />
      )}

      {/* Navigation - WCAG AAA Semantic HTML */}
      <nav 
        id="navigation" 
        className={`navigation ${isMenuOpen ? 'open' : ''}`}
        role="navigation"
        aria-label="Menu główne"
      >
        {/* Na stronach auth - renderuj jako przyciski soft */}
        {isAuthPage ? (
          navLinks.map(link => (
            <Link 
              key={link.path}
              to={link.path} 
              className="nav-button"
              onClick={handleLinkClick}
            >
              {link.label}
            </Link>
          ))
        ) : (
          /* Na zwykłych stronach - renderuj jako linki z podkreśleniem lub dropdown */
          navLinks.map(link => {
            if (link.dropdown) {
              const dropdownKey = link.path
              const isOpen = isDropdownOpen(dropdownKey)
              const isParentActive = isActive(link.path) === 'active' || isAnyDropdownItemActive(link.dropdown)
              // Desktop: hover dropdown, Mobile: click dropdown
              return (
                <div 
                  key={link.path}
                  className="nav-dropdown"
                  onMouseEnter={() => setDropdownOpen(dropdownKey, true)}
                  onMouseLeave={() => setDropdownOpen(dropdownKey, false)}
                >
                  <Link 
                    to={link.path} 
                    className={`nav-link ${link.emphasis ? 'nav-link--primary' : ''} ${isParentActive ? 'active' : ''}`}
                    onClick={(e) => {
                      if (isMobile) {
                        e.preventDefault()
                        toggleDropdown(dropdownKey)
                      } else {
                        handleLinkClick()
                      }
                    }}
                  >
                    {link.label}
                    <svg 
                      className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
                      width="12" 
                      height="12" 
                      viewBox="0 0 12 12" 
                      fill="none"
                      aria-hidden="true"
                    >
                      <path 
                        d="M2 4L6 8L10 4" 
                        stroke="currentColor" 
                        strokeWidth="1.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                  {isOpen && (
                    <div className="nav-dropdown-menu" aria-hidden={false}>
                      <div className="nav-dropdown-panel" role="menu" aria-label={`${link.label} - podmenu`}>
                        {link.dropdown.map(item => {
                          const isItemActive = isPathActive(item.path)
                          return (
                            <Link 
                              key={item.path}
                              to={item.path}
                              className={`nav-dropdown-item ${isItemActive ? 'active' : ''}`}
                              onClick={handleLinkClick}
                              role="menuitem"
                              aria-current={isItemActive ? 'page' : undefined}
                            >
                              {item.label}
                            </Link>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )
            }
            return (
              <Link 
                key={link.path}
                to={link.path} 
                className={`nav-link ${link.emphasis ? 'nav-link--primary' : ''} ${isActive(link.path)}`}
                onClick={handleLinkClick}
              >
                {link.label}
              </Link>
            )
          })
        )}
        
        {/* Auth tylko gdy NIE jesteśmy na login/register */}
        {!isAuthPage && (
          <>
            {user ? (
              <UserMenu onMenuClick={handleLinkClick} isMobile={isMobile} />
            ) : (
              <Link to="/logowanie" className="nav-button" onClick={handleLinkClick}>
                Zaloguj się
              </Link>
            )}
          </>
        )}
      </nav>
    </>
  )
}

export default Navigation

